import { chooseZoomForRadius, GeoReference, latLonToGlobalPx, metersPerPixel } from './coordinateConversion'
import { DEFAULT_GREEN_AUTO_MAP_PARAMS, GreenAutoMapParams } from './parameterSelection'
import { runEdgesAndPlot } from './runEdgesAndPlot'

const GOOGLE_STATIC_MAP_MAX_SIZE_PX = 640
const NOTEBOOK_ZOOM_MIN = 15
const SINGLE_TILE_TARGET_RADIUS_PX = GOOGLE_STATIC_MAP_MAX_SIZE_PX / 2

export interface GpsCoordinate {
  lat: number
  lng: number
}

export interface GreenAutoMapHoleInput {
  green: GpsCoordinate
  teebox: GpsCoordinate
}

export interface RunGreenAutoMapForHoleArgs extends GreenAutoMapHoleInput {
  googleMapsApiKey?: string | null
  params?: Partial<GreenAutoMapParams>
}

export { DEFAULT_GREEN_AUTO_MAP_PARAMS }
export type { GreenAutoMapParams }

function looksLikeNoData(imageData: ImageData): boolean {
  const values: number[] = []

  for (let index = 0; index < imageData.data.length; index += 4) {
    const gray = (imageData.data[index] + imageData.data[index + 1] + imageData.data[index + 2]) / 3
    values.push(gray)
  }

  if (values.length === 0) {
    return true
  }

  const mean = values.reduce((sum, value) => sum + value, 0) / values.length
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length

  return Math.sqrt(variance) < 8 && mean > 140
}

async function fetchStaticMapImage(
  centerLat: number,
  centerLng: number,
  zoom: number,
  widthPx: number,
  heightPx: number,
  googleMapsApiKey: string,
): Promise<ImageData> {
  const url =
    `https://maps.googleapis.com/maps/api/staticmap?` +
    `center=${centerLat},${centerLng}&zoom=${zoom}` +
    `&size=${widthPx}x${heightPx}&maptype=satellite&key=${googleMapsApiKey}`

  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`Static map request failed with status ${response.status}`)
  }

  const blob = await response.blob()
  const bitmap = await createImageBitmap(blob)
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
  const context = canvas.getContext('2d')

  if (!context) {
    throw new Error('Unable to create 2D context for static map fetch')
  }

  context.drawImage(bitmap, 0, 0)

  return context.getImageData(0, 0, bitmap.width, bitmap.height)
}

async function fetchSingleTileRoiSatelliteWithFallback(
  centerLat: number,
  centerLng: number,
  sideSizeM: number,
  zoomStart: number,
  googleMapsApiKey: string,
  zoomMin: number = NOTEBOOK_ZOOM_MIN,
  debugImageLogging: boolean = false,
): Promise<{ roiRgb: ImageData; geoRef: GeoReference }> {
  let lastError: unknown = null

  for (let zoom = zoomStart; zoom >= zoomMin; zoom--) {
    try {
      const mpp = metersPerPixel(centerLat, zoom)
      const sidePx = Math.max(1, Math.round(sideSizeM / mpp))

      if (sidePx > GOOGLE_STATIC_MAP_MAX_SIZE_PX) {
        continue
      }

      const [centerXg, centerYg] = latLonToGlobalPx(centerLat, centerLng, zoom)
      const halfWidthPx = sidePx / 2
      const halfHeightPx = sidePx / 2
      const x0g = centerXg - halfWidthPx
      const y0g = centerYg - halfHeightPx

      const roiRgb = await fetchStaticMapImage(centerLat, centerLng, zoom, sidePx, sidePx, googleMapsApiKey)

      if (looksLikeNoData(roiRgb)) {
        continue
      }

      if (debugImageLogging) {
        console.log(`[gpsUtils] single-tile ROI ${sidePx}x${sidePx} for ${sideSizeM}m at zoom ${zoom}`)
      }

      return {
        roiRgb,
        geoRef: {
          zoom,
          mpp,
          x0g,
          y0g,
          H: roiRgb.height,
          W: roiRgb.width,
        },
      }
    } catch (error) {
      lastError = error
    }
  }

  const errorSuffix = lastError instanceof Error ? `: ${lastError.message}` : ''
  throw new Error(`Could not fetch single-tile ROI imagery down to zoom ${zoomMin}${errorSuffix}`)
}

export async function runGreenAutoMapForHole({ green, googleMapsApiKey, params }: RunGreenAutoMapForHoleArgs) {
  if (!googleMapsApiKey) {
    throw new Error('Google Maps API key is required for green auto-map')
  }

  const mergedParams: GreenAutoMapParams = {
    ...DEFAULT_GREEN_AUTO_MAP_PARAMS,
    ...params,
  }

  const searchRadiusM = mergedParams.maxSearchRadiusM
  const zoomStart = chooseZoomForRadius(
    green.lat,
    searchRadiusM,
    SINGLE_TILE_TARGET_RADIUS_PX,
    NOTEBOOK_ZOOM_MIN,
    22,
    22,
  )
  const { roiRgb, geoRef } = await fetchSingleTileRoiSatelliteWithFallback(
    green.lat,
    green.lng,
    searchRadiusM * 2,
    zoomStart,
    googleMapsApiKey,
    NOTEBOOK_ZOOM_MIN,
    mergedParams.debugImageLogging,
  )

  return runEdgesAndPlot({
    coordinates: { lat: green.lat, lon: green.lng },
    ...mergedParams,
    maxSearchRadiusM: searchRadiusM,
    roiRgb,
    geoRef,
  })
}
